import React, { useEffect, useState } from 'react';
import { Form } from 'react-bootstrap';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import * as Yup from 'yup';

import { PSInput } from '@/components/ui/input';

export type INote = {
  note_id: string;
  creation_date_time: string;
  title: string;
  description: string;
  qc: boolean;
  author: string;
};

export interface INoteForm {
  notes: Array<INote>;
  showNoteForm: boolean;
}

export const getNoteSchema = t => ({
  notes: Yup.array(
    Yup.object({
      title: Yup.string()
        .max(50, t('Wizard/validationLabels/key24'))
        .when('description', {
          is: description => description !== '',
          then: schema => schema.required(t('Wizard/validationLabels/key26')),
          otherwise: schema => schema.optional(),
        }),
      description: Yup.string().max(250, t('Wizard/validationLabels/key25')),
      note_id: Yup.string(),
      creation_date_time: Yup.string(),
      author: Yup.string(),
      qc: Yup.boolean(),
    })
  ),
  showNoteForm: Yup.boolean().default(false),
});

const NoteForm = ({ noteFormMethods, preview = false, data }: { noteFormMethods?: any; preview?: boolean; data?: Array<INote> }) => {
  if (preview) {
    return data?.map((item, index) => (
      <div
        translate="no"
        style={{ whiteSpace: 'pre-line', overflowY: 'hidden' }}
        key={item.note_id}
        className={`tw-absolute tw-top-16 tw-p-4 tw-left-[-25px] md:tw-left-[-30px] tw-w-60 tw-h-60 tw-bg-amber-200 tw-shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] tw-shadow-lg tw-flex-col tw-justify-start tw-items-start  tw-text-left`}
      >
        <div className="tw-w-full tw-h-7 tw-pt-1 tw-overflow-hidden tw-flex tw-grow tw-shrink tw-basis-0 tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none  tw-bg-transparent tw-border-none note-form-control">
          {item.title}
        </div>
        <div className="tw-self-stretch tw-overflow-hidden tw-pt-2 tw-inline-flex tw-w-full tw-grow tw-shrink tw-basis-0 tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-bg-transparent tw-border-none note-form-control">
          {item?.description}
        </div>
      </div>
    ));
  }

  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState(false);
  const { register, watch, errors, fields, setValue } = noteFormMethods;
  useEffect(() => {
    setIsVisible(watch(`showNoteForm`) && isMobile);
  }, [watch(`showNoteForm`)]);

  const renderForm = (item, index) => {
    return (
      <React.Fragment key={index}>
        <Form.Group className="tw-w-full tw-self-stretch tw-justify-between tw-items-center">
          <Form.Control
            isInvalid={!!errors.notes?.[index]?.title}
            maxLength={50}
            placeholder={t('NoteForm/noteTitle')}
            {...register(`notes.${index}.title`)}
            id={`notes.${index}.title`}
            className="tw-w-full tw-h-7 tw-mt-1 md:tw-mt-4 tw-justify-center tw-items-center tw-gap-2.5 tw-flex tw-grow tw-shrink tw-basis-0 tw-text-gray-800 tw-text-xs tw-font-normal tw-font-['Lexend'] tw-leading-none  tw-bg-transparent tw-border-none note-form-control"
          />
          <Form.Control.Feedback type="invalid" className="tw-px-3  tw-text-xs  tw-font-s">
            {errors.notes?.[index]?.title?.message as string}
          </Form.Control.Feedback>
        </Form.Group>

        <Form.Group className="tw-w-full tw-self-stretch tw-justify-between tw-items-center">
          <Form.Control
            isInvalid={!!errors.notes?.[index]?.description}
            maxLength={250}
            as="textarea"
            rows="9"
            placeholder={t('NoteForm/noteDescriptionPlaceHolder')}
            {...register(`notes.${index}.description`)}
            id={`notes.${index}.description`}
            className="tw-resize-none tw-self-stretch tw-mt-2 tw-justify-center tw-items-center tw-gap-2.5 tw-inline-flex tw-w-full tw-grow tw-shrink tw-basis-0 tw-text-gray-800 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight tw-bg-transparent tw-border-none note-form-control"
          />
        </Form.Group>

        {/* Hidden inputs to ensure these values are included in form submission */}
        <PSInput type="hidden" {...register(`notes.${index}.note_id`)} value={item.note_id} />
        <PSInput type="hidden" {...register(`notes.${index}.creation_date_time`)} value={item.creation_date_time} />
        <PSInput type="hidden" {...register(`notes.${index}.qc`)} value={item.qc ?? false} />
        <PSInput type="hidden" {...register(`notes.${index}.author`)} value={item.author} />
      </React.Fragment>
    );
  };
  return (
    <React.Fragment>
      {watch(`showNoteForm`) === true &&
        fields?.map((item, index) => (
          <div
            key={item.note_id}
            className={`tw-absolute tw-top-24 tw-hidden md:tw-block md:tw-left-[-245px] tw-w-60 tw-h-60 tw-bg-amber-200 tw-shadow-[0px_4px_6px_-2px_rgba(0,0,0,0.05)] tw-shadow-lg tw-flex-col tw-justify-between tw-items-start tw-inline-flex`}
          >
            {renderForm(item, index)}
          </div>
        ))}
      {!!errors.notes && <span className="d-block text-danger">{errors.notes.message}</span>}

      {isVisible && (
        <div
          role="button"
          tabIndex={0}
          className="tw-block md:tw-hidden tw-absolute tw-w-full tw-h-screen tw-bg-neutral-800 tw-bg-opacity-50 tw-cursor-pointer"
          onClick={() => {
            setIsVisible(false);
            setValue?.('showNoteForm', false);
          }}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              setIsVisible(false);
              setValue?.('showNoteForm', false);
            }
          }}
        >
          <div
            role="presentation"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => e.stopPropagation()}
            className="tw-absolute tw-bottom-0 tw-w-full tw-h-[330px] tw-bg-amber-200 tw-transition tw-rounded-t-2xl tw-overflow-hidden tw-pt-6"
          >
            {watch(`showNoteForm`) === true && fields?.map((item, index) => renderForm(item, index))}
            {!!errors.notes && <span className="d-block text-danger">{errors.notes.message}</span>}
          </div>
        </div>
      )}

      {isMobile &&
        errors?.notes?.map((item, index) => (
          <span key={index} className="tw-px-3 tw-pb-4 d-block text-danger">
            {item?.title?.message}
          </span>
        ))}
    </React.Fragment>
  );
};

export default NoteForm;
